"use client";

import { useEffect, useRef, useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { Loader2, Search as SearchIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const DEBOUNCE_MS = 250;

interface SearchBarProps {
  initialQuery: string;
}

function buildUrl(query: string): string {
  const trimmed = query.trim();
  return trimmed ? `/search?q=${encodeURIComponent(trimmed)}` : "/search";
}

export default function SearchBar({ initialQuery }: SearchBarProps) {
  const router = useRouter();
  const [value, setValue] = useState(initialQuery);
  const [isPending, startTransition] = useTransition();
  const isFirstRender = useRef(true);

  useEffect(() => {
    setValue(initialQuery);
  }, [initialQuery]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const handle = setTimeout(() => {
      const url = buildUrl(value);
      startTransition(() => router.replace(url));
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [value, router]);

  const navigateNow = (next: string) => {
    startTransition(() => router.replace(buildUrl(next)));
  };

  return (
    <form
      action="/search"
      role="search"
      className="relative"
      onSubmit={(e) => {
        e.preventDefault();
        navigateNow(value);
      }}
    >
      {isPending ? (
        <Loader2
          aria-hidden
          className="text-foreground pointer-events-none absolute top-1/2 left-4 -translate-y-1/2 animate-spin"
          size={18}
          strokeWidth={1.5}
        />
      ) : (
        <SearchIcon
          aria-hidden
          className="text-muted-foreground pointer-events-none absolute top-1/2 left-4 -translate-y-1/2"
          size={18}
          strokeWidth={1.5}
        />
      )}
      <Input
        name="q"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Search products…"
        aria-label="Search products"
        className="h-12 w-full pr-28 pl-12 text-sm tracking-wide"
      />
      <Button
        type="submit"
        size="sm"
        className="absolute top-1/2 right-2 h-9 -translate-y-1/2 px-4 text-[10px] font-bold tracking-[0.2em] uppercase"
      >
        Search
      </Button>
    </form>
  );
}
